package com.bulut.mecitogullari.core.export

import com.bulut.mecitogullari.core.export.ReportHtml.escape
import com.bulut.mecitogullari.core.export.ReportHtml.money
import com.bulut.mecitogullari.core.i18n.Localization
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.core.i18n.localizedCategory
import com.bulut.mecitogullari.core.util.formatDate
import com.bulut.mecitogullari.core.util.todayUtcMidnightMillis
import com.bulut.mecitogullari.data.model.CashTransaction
import com.bulut.mecitogullari.data.model.MonthSummary
import com.bulut.mecitogullari.data.model.TransactionType
import kotlin.math.abs

/** Printable monthly ledger: carry-over plus every entry of the month. */
object MonthReportHtml {

    fun build(
        summary: MonthSummary,
        transactions: List<CashTransaction>,
        projectNames: Map<String, String>,
    ): String {
        val s = Localization.strings
        return ReportHtml.page(
            title = "${summary.month.label} - ${s.pdfMonthlyReport}",
            body = buildString {
                append("<h1>${escape(summary.month.label)}</h1>")
                append(
                    "<p class=\"subtitle\">${s.pdfMonthlyReportSubtitle} • " +
                        "${formatDate(todayUtcMidnightMillis())} • " +
                        s.pdfTransactionCount.fillTemplate("count" to transactions.size.toString()) +
                        "</p>"
                )

                if (transactions.isNotEmpty()) {
                    append("<table>")
                    append(
                        "<thead><tr><th>${s.date}</th><th>${s.description}</th>" +
                            "<th>${s.category}</th><th>${s.project}</th>" +
                            "<th class=\"num\">${s.income}</th>" +
                            "<th class=\"num\">${s.expense}</th></tr></thead><tbody>"
                    )
                    transactions.sortedBy { it.dateEpochMillis }.forEach { tx ->
                        val isIncome = tx.type == TransactionType.INCOME
                        append("<tr>")
                        append("<td>${formatDate(tx.dateEpochMillis)}</td>")
                        append("<td>${escape(tx.description.ifBlank { s.none })}</td>")
                        append("<td class=\"muted\">${escape(localizedCategory(tx.category))}</td>")
                        append(
                            "<td class=\"muted\">${
                                escape(tx.projectId?.let { projectNames[it] } ?: s.none)
                            }</td>"
                        )
                        append("<td class=\"num credit\">${if (isIncome) money(tx.amount) else ""}</td>")
                        append("<td class=\"num debit\">${if (!isIncome) money(tx.amount) else ""}</td>")
                        append("</tr>")
                    }
                    append("<tr class=\"total\">")
                    append("<td colspan=\"4\">${s.total}</td>")
                    append("<td class=\"num credit\">${money(summary.income)}</td>")
                    append("<td class=\"num debit\">${money(summary.expense)}</td>")
                    append("</tr></tbody></table>")
                }

                append("<table class=\"summary\">")
                append("<tr><td>${s.pdfCarriedBalance}</td><td class=\"num\">${money(summary.openingBalance)}</td></tr>")
                append("<tr><td>${s.pdfTotalIncome}</td><td class=\"num credit\">${money(summary.income)}</td></tr>")
                append("<tr><td>${s.pdfTotalExpense}</td><td class=\"num debit\">−${money(summary.expense)}</td></tr>")
                val net = summary.netProfit
                append(
                    "<tr><td>${s.pdfMonthlyNet}</td><td class=\"num ${if (net < 0) "debit" else "credit"}\">" +
                        "${if (net < 0) "−" else ""}${money(abs(net))}</td></tr>"
                )
                append(
                    "<tr class=\"total\"><td>${s.pdfClosingBalance}</td>" +
                        "<td class=\"num\">${money(summary.closingBalance)}</td></tr>"
                )
                append("</table>")
            },
        )
    }
}
